import React, { useState, useEffect } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './Dashboard.css';
import { API_BASE, assetUrl } from "../apiConfig";

const FlyerCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  // WCAG 2.2.2 Pause, Stop, Hide: auto-rotation stops while the carousel has
  // keyboard focus or the pointer is over it, and can be paused explicitly.
  const [isPaused, setIsPaused] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch(`${API_BASE}/therapist_api/get_banner_data.php`);
        const result = await response.json();
        
        if (result && result.data) {
          setBanners(result.data);
        }
      } catch (error) {
        console.error("Error fetching banners:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    if (banners.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [banners.length, isPaused]);

  const selectSlide = (index) => {
    setActiveIndex(index);
    const title = banners[index]?.title;
    setAnnouncement(`Slide ${index + 1} of ${banners.length}${title ? `: ${title}` : ""}`);
  };

  const handlePrev = () => {
    selectSlide((activeIndex - 1 + banners.length) % banners.length);
  };

  const handleNext = () => {
    selectSlide((activeIndex + 1) % banners.length);
  };

  if (loading) {
    return (
      <div className="text-center p-5 text-secondary" role="status">
        <div className="spinner-border text-primary" aria-hidden="true"></div>
        <p className="mt-2">Loading Banners...</p>
      </div>
    );
  }

  if (banners.length === 0) {
    return <div className="text-center p-5 text-muted">No banners available.</div>;
  }

  const handleKeyDown = (e) => {
    if (banners.length <= 1) return;
    
    if (e.key === 'ArrowLeft') {
      handlePrev();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  };

  const showControls = banners.length > 1;

  return (
    <div role="region" aria-label="Banner carousel" aria-roledescription="carousel">
      <div 
        className="flyer-carousel" 
        tabIndex={showControls ? 0 : undefined}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setIsPaused(false);
        }}
        aria-live="off"
      >
        <div
          className="carousel-inner"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          role="group"
          aria-roledescription="slide"
          aria-label={`Slide ${activeIndex + 1} of ${banners.length}`}
        >
          {banners.map((item, index) => (
            <div
              key={item.id || index}
              className="carousel-item-custom"
              role="group"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1}: ${item.title || 'Banner'}`}
              aria-hidden={index !== activeIndex}
            >
              {/* get_banner_data.php returns a path relative to the app folder,
                  so assetUrl() prefixes it with API_BASE. */}
              <img src={assetUrl(item.banner_image_url)} alt={item.title || 'Banner image'} />
            </div>
          ))}
        </div>

        {showControls && (
          <>
            <button
              className="carousel-control-custom carousel-control-prev-custom"
              onClick={handlePrev}
              aria-label="Previous slide"
              type="button"
            >
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
            <button
              className="carousel-control-custom carousel-control-next-custom"
              onClick={handleNext}
              aria-label="Next slide"
              type="button"
            >
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </>
        )}
      </div>

      {showControls && (
        <div className="carousel-dots-outside" role="group" aria-label="Choose a slide">
          {banners.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`dot ${index === activeIndex ? 'active' : ''}`}
              aria-label={`Go to slide ${index + 1} of ${banners.length}`}
              aria-current={index === activeIndex ? "true" : undefined}
              onClick={() => selectSlide(index)}
            ></button>
          ))}
          <button
            type="button"
            className="carousel-pause-btn"
            onClick={() => setIsPaused((paused) => !paused)}
            aria-label={isPaused ? "Play slideshow" : "Pause slideshow"}
          >
            <i className={`fas ${isPaused ? "fa-play" : "fa-pause"}`} aria-hidden="true"></i>
          </button>
        </div>
      )}

      {/* Screen-reader status for manual slide changes only; auto-rotation
          stays silent so it does not interrupt the page. */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
    </div>
  );
};

export default FlyerCarousel;

